import { Injectable } from '@angular/core';
import { map, take } from 'rxjs/operators';
import { ShipmentRepository } from '@data-layer/repositories';
import { Router } from '@angular/router';
import { PaginatedResultModel } from '@data-layer/models/common';
import { LclShipmentCardModel } from '@data-layer/models/lcl/lcl-shipment-card.model';
import { MarineInsuranceManagementModel } from '@data-layer/models/marine-insurance';
import { MarineInsuranceFacade } from '@shared/marine-insurance-shared/services';
import { ManagementShipmentModel } from '@data-layer/models/management';

@Injectable({
  providedIn: 'root',
})
export class BillListService {
  constructor(
    private router: Router,
    private shipmentRepository: ShipmentRepository,
    private marineInsuranceFacade: MarineInsuranceFacade,
  ) {}

  public redirect(referenceNumber: string, companyId: number, customsBondSectionId: string = 'customs-bond-section'): void {
    const replacedReference: string = referenceNumber.replace(/\d+/gi, '');
    switch (replacedReference) {
      case 'ES':
        this.router.navigate(['/entry-summary', referenceNumber]);
        break;
      case 'ISF':
        this.router.navigate(['/isf', referenceNumber]);
        break;
      case 'BR':
        this.router.navigate(['/companies', 'account-settings', 'company-view', companyId], { fragment: customsBondSectionId });
        break;
      case 'SH':
        this.getShipmentIdByReferenceNumber(referenceNumber, companyId);
        break;
      default:
        return;
    }
  }

  public getShipmentIdByReferenceNumber(referenceNumber: string, companyId: number): void {
    this.shipmentRepository
      .getAll(companyId)
      .pipe(
        map((shipmentItems: PaginatedResultModel<LclShipmentCardModel>) => {
          return (shipmentItems?.items || []).filter((shipment: LclShipmentCardModel) => {
            return shipment.referenceNumber === referenceNumber;
          })?.[0];
        }),
        take(1),
      )
      .subscribe((shipment: LclShipmentCardModel) => {
        if (!!shipment) {
          this.router.navigate(['/lcl-shipping/', shipment.id, 'shipment']);
        }
      });
  }

  public getShipmentManagementIdByReferenceNumber(referenceNumber: string): void {
    this.shipmentRepository
      .searchManagement()
      .pipe(
        map((shipmentItems: PaginatedResultModel<ManagementShipmentModel>) => {
          return shipmentItems.items.filter((shipment: ManagementShipmentModel) => {
            return shipment.referenceNumber === referenceNumber;
          })[0];
        }),
        take(1),
      )
      .subscribe((shipment: ManagementShipmentModel) => {
        this.router.navigate(['/shipments-management/', shipment.id]);
      });
  }

  public getMarineInsuranceIdByReferenceNumber(referenceNumber: string): void {
    this.marineInsuranceFacade
      .getManagementShipments({ referenceNumber })
      .pipe(
        map((insuranceItems: PaginatedResultModel<MarineInsuranceManagementModel>) => {
          return insuranceItems.items.filter((item: MarineInsuranceManagementModel) => {
            return item.referenceNumber === referenceNumber;
          })[0];
        }),
        take(1),
      )
      .subscribe((shipment: MarineInsuranceManagementModel) => {
        this.router.navigate(['/marine-insurance-management/', shipment.id]);
      });
  }
}
